import { bridge, coreEngine, Entity, env, store } from "@dinahmoe/dmafjs-core";

class Root extends Entity {
  namespace = "visual:root";
  engine = "visual";

  init(cNode, event) {
    super.init(cNode, event);

    cNode.props["screen.width"] = 0;
    cNode.props["screen.height"] = 0;
    cNode.props.visualInit = false;

    bridge.create({ ...this.getInitData(cNode), uid: "visual/" + cNode.uid, sender: "core" });
    cNode.props.orientationWarning = null;
  }

  receiveMessage(cNode, message) {
    switch (message.key) {
      case "visibility": {
        this._receiveVisibilityEvent(cNode, message);
        break;
      }
      case "keyevent": {
        this._receiveKeyEvent(cNode, message);
        break;
      }
      case "resize": {
        this._receiveResizeEvent(cNode, message);
        break;
      }
      case "orientation": {
        this._updateOrientation(cNode, message);
        break;
      }
      case "embedded": {
        this._receiveEmbeddedEvent(cNode, message);
        break;
      }
      case "windowfocus": {
        this._receiveWindowFocusEvent(cNode, message);
        break;
      }
    }
  }

  _receiveVisibilityEvent(cNode, message) {
    cNode["appearance.visibility"] = message.value.visibility;
    this.dispatch(`visibility:${cNode["appearance.visibility"]}`);
  }

  _receiveWindowFocusEvent(cNode, message) {
    cNode.windowFocused = message.value.focused;
    this.dispatch(`windowfocus:${cNode.windowFocused ? 'focused' : 'blurred'}`);
  }

  _receiveKeyEvent(cNode, message) {
    const eventKey = message.value.key;
    // Handle spacebar special case - check for "space" in config if actual key is " "
    const configKey = eventKey === " " ? "space" : eventKey;
    
    // Update lastKeyEvent property with the current key event
    cNode["keyboard.lastEvent"] = `${configKey}:${message.value.type}`;

    if (cNode.props["keyboard.events"] && Array.isArray(cNode.props["keyboard.events"])) {
      if (cNode.props["keyboard.events"].includes(configKey)) {
        this.dispatch(`keyevent:${configKey}:${message.value.type}`);
      }
    }
  }

  _receiveResizeEvent(cNode, message) {
    if (!cNode.props.visualInit) {
      this.dispatch("visual_init");
      cNode.props.visualInit = true;
    }

    cNode["screen.width"] = message.value.width;
    cNode["screen.height"] = message.value.height;

    if (env.platform === "desktop") {
      this.resize(cNode);
    } else {
      cNode["size.width"] = cNode.props["screen.width"];
      cNode["size.height"] = cNode.props["screen.height"];
    }
  }

  _updateOrientation(cNode, message) {
    cNode["orientation.type"] = message.value.type;
    cNode["orientation.angle"] = message.value.angle;

    if (cNode["orientation.type"].includes("landscape")) {
      cNode["orientation.state"] = "landscape";
    }
    if (cNode["orientation.type"].includes("portrait")) {
      cNode["orientation.state"] = "portrait";
    }
    this.dispatch(cNode["orientation.state"]);

    let preferredOrientation;
    if (env.platform === "desktop") {
      return;
    }
    if (env.platform === "mobile") {
      preferredOrientation = cNode["orientation.preferredMobile"];
    }
    if (env.platform === "tablet") {
      preferredOrientation = cNode["orientation.preferredTablet"];
    }

    if (cNode["orientation.state"] !== preferredOrientation) {
      this._createOrientationWarning(cNode);
    } else {
      this._destroyOrientationWarning(cNode);
    }
  }

  _createOrientationWarning(cNode) {
    const template = store.get(`template.visual.rotate_screen`);
    if (!template) {
      console.error("root expects a template named rotate_screen");
      return;
    }
    template.parentUid = cNode.uid;
    template.uid = "rotate_screen";
    coreEngine.create(template);
  }

  _destroyOrientationWarning(cNode) {
    coreEngine.destroy("rotate_screen");
  }

  _receiveEmbeddedEvent(cNode, message) {
    cNode.isEmbedded = message.value.isEmbedded;
  }

  resize(cNode) {
    const scaleWidth = cNode.props["screen.width"] > cNode.props["size.widthMax"];
    const scaleHeight = cNode.props["screen.height"] > cNode.props["size.heightMax"];

    let scalarWidth, scalarHeight, height, width, domScale;

    if (!scaleWidth && !scaleHeight) {
      domScale = cNode.props["scale.props"];
      width = cNode.props["screen.width"] / domScale;
      height = cNode.props["screen.height"] / domScale;
    } else {
      scalarWidth = cNode.props["screen.width"] / cNode.props["size.widthMax"];
      scalarHeight = cNode.props["screen.height"] / cNode.props["size.heightMax"];

      if (scaleWidth && scaleHeight) {
        if (scalarHeight > scalarWidth) {
          domScale = scalarHeight * cNode.props["scale.props"];
          height = cNode.props["size.heightMax"] / cNode.props["scale.props"];
          width = cNode.props["screen.width"] / domScale;
        } else {
          domScale = scalarWidth * cNode.props["scale.props"];
          width = cNode.props["size.widthMax"] / cNode.props["scale.props"];
          height = cNode.props["screen.height"] / domScale;
        }
      } else if (scalarHeight > scalarWidth) {
        domScale = scalarHeight * cNode.props["scale.props"];
        width = cNode.props["screen.width"] / domScale;
        height = cNode.props["size.heightMax"] / cNode.props["scale.props"];
      } else {
        domScale = scalarWidth * cNode.props["scale.props"];
        width = cNode.props["size.widthMax"] / cNode.props["scale.props"];
        height = cNode.props["screen.height"] / domScale;
      }
    }

    // TODO to look at, causes issues on larger root sizes and odd dimensions
    width = Math.max(width, (cNode.props["size.widthMin"]) ?? width);
    height = Math.max(height, (cNode.props["size.heightMin"]) ?? height);

    cNode["scale.dom"] = domScale;
    cNode["size.width"] = width;
    cNode["size.height"] = height;
  }

}

const root = new Root();
export { Root, root };
